import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, auth as authApi } from "@/lib/api";

/**
 * `/reset-password?token=<tok>` — landing page for the password-reset link
 * Skarp emails after `/api/auth/forgot_password`. Form takes a new password
 * + confirmation; on success, sends the user to /login (no auto-login —
 * we want them to enter the password they just set, both as a sanity
 * check and to avoid a long-lived browser session paired with a token
 * fresh from an email).
 */
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const validate = (): string | null => {
    if (!token.trim()) {
      return "This reset link is missing its token. Open the link from your email again, or request a new one.";
    }
    if (!password) return "Choose a new password.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (password !== confirm) return "Passwords don't match.";
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setBusy(true);
    try {
      const res = await authApi.resetPassword(token, password);
      if (res.ok) {
        setDone(true);
      } else {
        setError(res.message ?? "We couldn't reset your password. Try requesting a fresh link.");
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string; error?: string } | null;
        setError(body?.message ?? body?.error ?? `Reset failed (${err.status})`);
      } else {
        setError(String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  // Bad-link state — no token in the URL at all.
  if (!token) {
    return (
      <Shell>
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-semibold mb-2">Bad link</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
            This reset link is missing its token. Open the link from your email
            again, or request a new one from the sign-in page.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium"
          >
            Back to sign in
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center max-w-sm">
          <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-2xl mb-3">
            ✓
          </div>
          <h1 className="text-2xl font-semibold mb-2">Password updated.</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
            You can sign in with your new password now.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:opacity-90"
          >
            Sign in
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-semibold mb-2">Set a new password</h1>
        <p className="text-sm text-zinc-500 mb-6">
          Choose a new password for your Coauthor account.
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">New password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              placeholder="At least 8 characters"
              className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Confirm new password</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Re-enter the password"
              className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
            />
          </label>
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>

        <div className="border-t border-zinc-200 dark:border-zinc-800 mt-6 pt-4 text-sm text-zinc-500 text-center">
          <Link to="/login" className="underline hover:opacity-80">
            Back to sign in
          </Link>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            ← Coauthor
          </Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        {children}
      </div>
    </div>
  );
}
